/**
 * CopilotProvider — owns the copilot chat state and drives the agent loop.
 *
 * Mounted at the app root (outside App's startup↔project switch) so the
 * conversation survives the transition from onboarding into a project. The
 * agent loop runs outside React via the actionBridge, so this provider only
 * needs to hold messages + live step activity and expose sendMessage / undo.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { runAgent } from '../../services/agent/agentLoop';
import { callAction } from '../../services/agent/actionBridge';

const CopilotContext = createContext(null);

// Docked, the copilot takes 360px. Below this window width that leaves the
// project too little room, so it starts collapsed there.
const DOCK_OPEN_MIN_WIDTH = 1440;

export const useCopilot = () => {
  const ctx = useContext(CopilotContext);
  if (!ctx) throw new Error('useCopilot must be used within a CopilotProvider');
  return ctx;
};

export function CopilotProvider({ children }) {
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant'|'error', text }
  const [activity, setActivity] = useState([]); // live agent steps for the current run
  const [isRunning, setIsRunning] = useState(false);
  const [open, setOpenState] = useState(false);
  const [docked, setDockedState] = useState(false); // true inside a project → fixed right panel
  const [undoable, setUndoable] = useState(false); // last run made a write the user can undo
  const dockedRef = useRef(false);
  const collapsedInProject = useRef(false); // the user closed the dock; keep it closed

  const setOpen = useCallback((v) => {
    if (dockedRef.current) collapsedInProject.current = !v;
    setOpenState(v);
  }, []);

  // Docking is driven by App (project ⇄ welcome). Entering a project keeps the
  // copilot open if it already was, and otherwise opens it only on a wide
  // window the user has not collapsed it on. Leaving collapses it back to the
  // floating launcher.
  const setDocked = useCallback((v) => {
    dockedRef.current = v;
    setDockedState(v);
    if (!v) { setOpenState(false); return; }
    const roomy = typeof window !== 'undefined' && window.innerWidth >= DOCK_OPEN_MIN_WIDTH;
    setOpenState((wasOpen) => wasOpen || (roomy && !collapsedInProject.current));
  }, []);

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || isRunning) return;

    const displayNext = [...messages, { role: 'user', text: trimmed }];
    setMessages(displayNext);
    setActivity([]);
    setIsRunning(true);

    // Build a clean transcript for the model (user/assistant only).
    const convo = displayNext
      .filter((m) => m.text && m.role !== 'error')
      .map((m) => ({ role: m.role === 'user' ? 'user' : 'assistant', text: m.text }));

    let didWrite = false;
    try {
      const result = await runAgent({
        history: convo,
        onStep: (step) => {
          if (step.type === 'action' && step.kind === 'write') didWrite = true;
          if (step.type !== 'final') setActivity((a) => [...a, step]);
        },
      });
      setMessages((m) => [...m, { role: result.error ? 'error' : 'assistant', text: result.text }]);
      if (didWrite) setUndoable(true);
    } catch (err) {
      setMessages((m) => [...m, { role: 'error', text: `Something went wrong: ${err.message}` }]);
    } finally {
      setIsRunning(false);
      setActivity([]);
    }
  }, [messages, isRunning]);

  const undo = useCallback(async () => {
    try {
      const res = await callAction('undo', {});
      setUndoable(false);
      setMessages((m) => [...m, { role: 'assistant', text: res?.label ? `Reverted: ${res.label}.` : 'Reverted the last change.' }]);
    } catch (_) {
      setMessages((m) => [...m, { role: 'assistant', text: 'Nothing to undo right now.' }]);
      setUndoable(false);
    }
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setActivity([]);
    setUndoable(false);
  }, []);

  const value = { messages, activity, isRunning, open, setOpen, docked, setDocked, sendMessage, undo, undoable, clearChat };
  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
}

export default CopilotProvider;
